/**
 * デモリセット確認ダイアログ
 *
 * データ削除前の安全確認とカスタムデータの表示
 */

import { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import {
  ExclamationTriangleIcon,
  CircleStackIcon,
  CalendarIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { demoResetService } from '../../services/demoResetService';
import logger from '../../utils/logger';

// シンプルな日付フォーマット関数
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    logger.warn('Invalid date string:', dateString);
    return dateString;
  }
};

// =============================================================================
// 型定義
// =============================================================================

interface CustomPartner {
  id: string;
  name: string;
  category: string;
  type: string;
  status: string;
  created_at: string;
  operation_type?: string;
}

interface BackupInfo {
  resetType?: string;
  currentGeneration?: number;
  templateTablesVerified?: boolean;
  templateRecordsSample?: number;
  resetMethod?: string;
  // Legacy fields for backward compatibility
  snapshotId?: string;
  createdAt?: string;
  tablesBackedUp?: string[];
  status?: string;
  sampleBackupRecords?: number;
}

interface DemoResetConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

// =============================================================================
// メインコンポーネント
// =============================================================================

export function DemoResetConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
}: DemoResetConfirmDialogProps) {
  const [loading, setLoading] = useState(true);
  const [customPartners, setCustomPartners] = useState<CustomPartner[]>([]);
  const [backupInfo, setBackupInfo] = useState<BackupInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasBackup, setHasBackup] = useState(false);

  // ダイアログが開かれた時にデータを取得
  useEffect(() => {
    if (isOpen) {
      loadConfirmationData();
    }
  }, [isOpen]);

  const loadConfirmationData = async () => {
    setLoading(true);
    setError(null);

    try {
      // バックアップ存在確認（テンプレート＋オーバーレイ方式）
      const backupCheck = await demoResetService.verifyBackupExists();
      setHasBackup(backupCheck.hasBackup);

      if (backupCheck.hasBackup && backupCheck.backupInfo) {
        setBackupInfo(backupCheck.backupInfo);
      } else {
        setError(backupCheck.error || 'テンプレートデータが見つかりません');
      }

      // 現在のオーバーレイデータ確認（テンプレート＋オーバーレイ方式）
      const customDataCheck = await demoResetService.getCustomDataAddedAfterSnapshot();
      if (customDataCheck.error) {
        logger.warn('Custom data check warning:', customDataCheck.error);
      }
      setCustomPartners(customDataCheck.customPartners || []);

      // スナップショット情報をログ出力
      if (customDataCheck.snapshotInfo) {
        logger.info('[DemoResetConfirmDialog] Reset info:', customDataCheck.snapshotInfo);
      }
    } catch (err) {
      logger.error('Error loading confirmation data:', err);
      setError(err instanceof Error ? err.message : '確認データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleCancel = () => {
    onCancel();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg'>
      <div className='p-6'>
        {/* ヘッダー */}
        <div className='flex items-center gap-3 mb-6'>
          <div className='p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full'>
            <ExclamationTriangleIcon className='w-6 h-6 text-amber-600 dark:text-amber-400' />
          </div>
          <div>
            <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
              デモデータリセットの確認
            </h2>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              テンプレート＋オーバーレイ方式によるデータリセット
            </p>
          </div>
        </div>

        {/* ローディング状態 */}
        {loading && (
          <div className='text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400'></div>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>確認データを取得中...</p>
          </div>
        )}

        {/* エラー状態 */}
        {error && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6'>
            <div className='flex items-center gap-2'>
              <ExclamationTriangleIcon className='w-5 h-5 text-red-600 dark:text-red-400' />
              <span className='font-medium text-red-800 dark:text-red-200'>リセットできません</span>
            </div>
            <p className='text-sm text-red-700 dark:text-red-300 mt-1'>{error}</p>
          </div>
        )}

        {/* メインコンテンツ */}
        {!loading && !error && (
          <div className='space-y-6'>
            {/* テンプレート＋オーバーレイ情報 */}
            {hasBackup && backupInfo && (
              <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4'>
                <div className='flex items-center gap-2 mb-2'>
                  <CircleStackIcon className='w-5 h-5 text-green-600 dark:text-green-400' />
                  <span className='font-medium text-green-800 dark:text-green-200'>
                    テンプレートデータによるリセット可能
                  </span>
                </div>
                <div className='text-sm text-green-700 dark:text-green-300 space-y-1'>
                  <div className='flex items-center gap-2'>
                    <CalendarIcon className='w-4 h-4' />
                    <span>リセット方式: {backupInfo.resetType || 'template_overlay'}</span>
                  </div>
                  <div>現在の世代: {backupInfo.currentGeneration || '不明'}</div>
                  <div>リセット後世代: {(backupInfo.currentGeneration || 1) + 1}</div>
                  {backupInfo.resetMethod && <div>実行方法: {backupInfo.resetMethod}</div>}
                </div>
              </div>
            )}

            {/* オーバーレイデータ情報 */}
            <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4'>
              <div className='flex items-center gap-2 mb-3'>
                <UsersIcon className='w-5 h-5 text-amber-600 dark:text-amber-400' />
                <span className='font-medium text-amber-800 dark:text-amber-200'>
                  現在のオーバーレイデータ
                </span>
              </div>

              {customPartners.length === 0 ? (
                <p className='text-sm text-amber-700 dark:text-amber-300'>
                  リセット対象のオーバーレイデータはありません（すでに初期状態です）
                </p>
              ) : (
                <div className='space-y-2'>
                  <p className='text-sm text-amber-700 dark:text-amber-300'>
                    以下の{customPartners.length}件のオーバーレイデータが非表示になります：
                  </p>
                  <div className='max-h-32 overflow-y-auto bg-white dark:bg-gray-800 rounded border border-amber-200 dark:border-amber-600'>
                    {customPartners.map(partner => (
                      <div
                        key={partner.id}
                        className='px-3 py-2 border-b border-amber-100 dark:border-gray-700 last:border-b-0'
                      >
                        <div className='flex justify-between items-center'>
                          <span className='font-medium text-gray-900 dark:text-white'>
                            {partner.name}
                          </span>
                          <span className='text-xs text-gray-500 dark:text-gray-400'>
                            {partner.category || 'データ'}
                          </span>
                        </div>
                        <p className='text-xs text-gray-600 dark:text-gray-400'>
                          作成日: {formatDate(partner.created_at)}
                          {partner.operation_type && ` (${partner.operation_type})`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 注意事項 */}
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600'>
              <h4 className='font-medium text-gray-900 dark:text-white mb-2'>
                テンプレート＋オーバーレイリセットについて
              </h4>
              <ul className='text-sm text-gray-600 dark:text-gray-300 space-y-1'>
                <li>• 世代番号をインクリメントすることで即座にリセット</li>
                <li>• オーバーレイデータが非表示になりテンプレートデータが表示</li>
                <li>• テンプレートデータは常に保護されており削除されません</li>
                <li>• 高速で安全なリセットが可能です</li>
              </ul>
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div className='flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600'>
          <button
            onClick={handleCancel}
            className='px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors'
          >
            キャンセル
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasBackup}
            className='px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors'
          >
            リセット実行
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default DemoResetConfirmDialog;
